import { useEffect, useLayoutEffect, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import { FontSize } from 'components/editor/GridDndEditor/TiptapEditor/extensions/font-size';

const secureEmailLinkExtension = Link.configure({
  openOnClick: false,
  autolink: true,
  defaultProtocol: 'https',
  protocols: ['http', 'https'],
  isAllowedUri: (url, ctx) => {
    try {
      const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`);
      const protocol = parsedUrl.protocol.replace(':', '');
      const allowedProtocols = ctx.protocols.map((p) => (typeof p === 'string' ? p : p.scheme));

      return allowedProtocols.includes(protocol) && ctx.defaultValidate(parsedUrl.href);
    } catch {
      return false;
    }
  },
});

interface UseEmailTemplateEditorOptions {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /** Applied to the ProseMirror root (e.g. share email form parity with `py-editor-body`). */
  editorClassName?: string;
}

export const useEmailTemplateEditor = ({
  value,
  onChange,
  placeholder = 'Enter email body...',
  editorClassName = 'py-editor-body',
}: UseEmailTemplateEditorOptions) => {
  // Store onChange in a ref so onUpdate always calls the latest version,
  // even though useEditor captures the callback only on mount.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editor = useEditor({
    editorProps: {
      attributes: {
        class: editorClassName,
      },
    },
    extensions: [
      Document,
      Paragraph,
      Heading,
      Text,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      FontFamily,
      FontSize,
      Bold,
      Italic,
      Underline,
      BulletList,
      OrderedList,
      ListItem,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      secureEmailLinkExtension,
      History,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChangeRef.current(editor.getHTML());
    },
  });

  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return;

    const nextValue = value || '';
    if (editor.getHTML() !== nextValue) {
      editor.commands.setContent(nextValue, false);
    }
  }, [editor, value]);

  return editor;
};
